/**
 * AssemblyStage.jsx — the home page's 3D stage.
 *
 * The same manifest-driven assembly as the exploded workspace, embedded in the
 * dashboard: opens assembled and slowly turning, explodes in place, and lets a
 * part be picked. "Open workspace" hands asset, explode and selection to the
 * full ExplodedView (tree, dossier, trace, ask); both share the GLTF cache, so
 * switching between them does not reload the model.
 *
 * State (asset, t, selectedId) is owned by the Dashboard so it survives the
 * stage unmounting while the workspace is open.
 *
 * Agent mode (CONTRACT §10.6), all optional: `overlayColors` (node id -> hex, from
 * agentHighlightColors) colours the scene, `caption` says why the stage moved, and
 * `topOffset` moves the controls strip (agent mode passes 12, under its own frame).
 * The part card and the caption sit below the strip's measured bottom: in agent mode's narrower
 * centre column the strip wraps to two or three rows, and a fixed offset would overlap it.
 */

import { useEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Spherical, Vector3 } from 'three';
import { OrbitControls } from '@react-three/drei';
import { Maximize2, RotateCcw } from 'lucide-react';

import ExplodedScene, { PROVENANCE_TEXT } from './ExplodedScene.jsx';
import CameraRig from './CameraRig.jsx';
import SceneLoadingOverlay from './SceneLoadingOverlay.jsx';
import { useAssembly } from './useAssembly.js';
import { sceneLoad, modelUrlFor, useSceneLoad } from './sceneLoadStatus.js';
import { useParts } from './knowledge/usePartKnowledge.js';
import { EXPLODED_ASSETS, explodedAssetFor } from '../../config/assets.js';
import './AssemblyStage.css';

const DEFAULT_TOP = 72;
const CARD_BELOW_BAR = 56;   // before the strip has been measured (one row)
const CARD_GAP = 12;

const LEVEL_LABEL = { assembly: 'Assembly', 'sub-assembly': 'Sub-assembly', unit: 'Part' };

/** frameloop="demand": nudge a render when a prop the scene reads in effects changes. */
function DemandNudge({ signal }) {
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => { invalidate(); }, [signal, invalidate]);
  return null;
}

const HOME_EASE_S = 1.1;

/**
 * The idle turn stops wherever it happens to be when the stage explodes or a part is picked;
 * from many of those angles the canopy panels hide the parts the demo is about. Ease back to
 * the asset's authored framing along the orbit (azimuth, elevation, distance), never through
 * the model. `active` goes false the moment the user takes the controls, which cancels it.
 */
function HomeReturn({ active, config, controlsRef }) {
  const camera = useThree((s) => s.camera);
  const invalidate = useThree((s) => s.invalidate);
  const anim = useRef(null);

  useEffect(() => {
    const controls = controlsRef.current;
    if (!active || !controls) { anim.current = null; return; }
    const target = new Vector3(...config.target);
    const from = new Spherical().setFromVector3(camera.position.clone().sub(controls.target));
    const to = new Spherical().setFromVector3(new Vector3(...config.position).sub(target));
    let dTheta = to.theta - from.theta;
    if (dTheta > Math.PI) dTheta -= 2 * Math.PI;
    if (dTheta < -Math.PI) dTheta += 2 * Math.PI;
    const still = Math.abs(dTheta) < 0.01 && Math.abs(to.phi - from.phi) < 0.01
      && Math.abs(to.radius - from.radius) < 0.02 && controls.target.distanceTo(target) < 0.01;
    anim.current = still ? null : { from, to, dTheta, target0: controls.target.clone(), target, t0: null };
    invalidate();
  }, [active, config, camera, controlsRef, invalidate]);

  useFrame(() => {
    const a = anim.current;
    const controls = controlsRef.current;
    if (!a || !controls) return;
    const now = performance.now() / 1000;
    if (a.t0 === null) a.t0 = now;
    const k = Math.min(1, (now - a.t0) / HOME_EASE_S);
    const e = k < 0.5 ? 2 * k * k : 1 - ((-2 * k + 2) ** 2) / 2;   // ease in-out
    controls.target.lerpVectors(a.target0, a.target, e);
    camera.position.setFromSpherical(new Spherical(
      a.from.radius + (a.to.radius - a.from.radius) * e,
      a.from.phi + (a.to.phi - a.from.phi) * e,
      a.from.theta + a.dTheta * e,
    )).add(controls.target);
    controls.update();
    if (k >= 1) anim.current = null;
    else invalidate();
  });
  return null;
}

/** Knowledge summaries cite parts as "Name [CAD_ID]"; the card shows names only. */
const cleanSummary = (text) => String(text ?? '').replace(/\s*\[[ASU]\d[^\]]*\]/g, '').trim();

function usePartSummary(asset, selectedId, tree) {
  const parts = useParts(asset.id, { enabled: asset.knowledge === 'part', tree });
  const [state, setState] = useState({ id: null, phase: 'idle', text: null });

  useEffect(() => {
    if (!parts || !selectedId) {
      setState({ id: null, phase: 'idle', text: null });
      return undefined;
    }
    const controller = new AbortController();
    setState({ id: selectedId, phase: 'loading', text: null });
    parts.dossier(selectedId, { signal: controller.signal })
      .then((r) => {
        if (controller.signal.aborted) return;
        setState(r?.ok
          ? { id: selectedId, phase: 'ready', text: r.data.summary || r.data.rollup_summary || null }
          : { id: selectedId, phase: 'unavailable', text: null });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ id: selectedId, phase: 'unavailable', text: null });
      });
    return () => controller.abort();
  }, [parts, selectedId]);

  return state.id === selectedId ? state : { id: null, phase: 'idle', text: null };
}

export default function AssemblyStage({
  assetId,
  onAssetChange,
  t = 0,
  onTChange,
  selectedId = null,
  onSelect,
  /** Soloed unit (the rest dimmed): a voice selection the default view would hide (isolateOnPick). */
  isolatedId = null,
  onOpenWorkspace,
  /** ({phase, pct}) for the dashboard's loading screen. */
  onLoadState,
  /** Live telemetry group for the selected part ({ title, metrics }) or null. */
  partTelemetry = null,
  /** Narrow layout while the chat takes the left of the screen. */
  compact = false,
  /** Agent highlight colours (Map node id -> hex) or null for identity colours. */
  overlayColors = null,
  /** Agent caption over the stage (why it moved), or null. */
  caption = null,
  /** Top of the controls strip in px (72 under the HUD bar; agent mode passes 12). */
  topOffset = DEFAULT_TOP,
}) {
  const asset = explodedAssetFor(assetId);
  const { manifest, tree, loading, error, reload } = useAssembly({ vehicle: asset.id });
  const manifestReady = manifest?.vehicle === asset.id;
  const modelUrl = manifestReady ? modelUrlFor(manifest) : null;
  const load = useSceneLoad(modelUrl);
  const partCount = manifestReady ? tree.byId.size : 0;

  const [sceneKey, setSceneKey] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const controlsRef = useRef(null);

  // Bottom of the controls strip (it wraps on a narrow stage); the card and caption go below it.
  const barRef = useRef(null);
  const [barBottom, setBarBottom] = useState(null);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    const update = () => setBarBottom(el.offsetTop + el.offsetHeight);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [topOffset]);
  const belowBar = barBottom !== null ? barBottom + CARD_GAP : topOffset + CARD_BELOW_BAR;

  // A new asset starts turning again.
  useEffect(() => { setInteracted(false); }, [asset.id]);

  const phase = load?.phase;
  const loaded = load?.loaded ?? 0;
  const total = load?.total ?? 0;
  useEffect(() => {
    if (typeof onLoadState !== 'function') return;
    if (error) { onLoadState({ phase: 'error' }); return; }
    if (!phase) return;
    let pct;
    if (phase === 'downloading' && total > 0) pct = Math.round((loaded / total) * 90);
    else if (phase === 'preparing') pct = 95;
    else if (phase === 'ready') pct = 100;
    onLoadState({ phase, pct });
  }, [phase, loaded, total, error, onLoadState]);

  const autoRotate = !interacted && !selectedId && t === 0 && phase === 'ready';
  // Left the idle turn without the user touching the view: go back to the authored framing.
  const returnHome = !interacted && phase === 'ready' && (t > 0 || Boolean(selectedId));

  const node = manifestReady && selectedId ? tree.byId.get(selectedId) ?? null : null;
  const summary = usePartSummary(asset, node ? selectedId : null, tree);

  const crumbs = [];
  for (let p = node?.parent ? tree.byId.get(node.parent) : null; p; p = p.parent ? tree.byId.get(p.parent) : null) {
    crumbs.unshift(p);
  }

  const resetView = () => {
    onTChange?.(0);
    onSelect?.(null);
    setInteracted(false);
    controlsRef.current?.reset?.();
  };

  return (
    <div className={`astage${compact ? ' is-compact' : ''}`}>
      <Canvas
        className="astage__canvas"
        frameloop={autoRotate ? 'always' : 'demand'}
        dpr={[1, 1.75]}
        camera={{ position: asset.camera.position, fov: 42, near: 0.05, far: asset.camera.far }}
        gl={{ antialias: true, powerPreference: 'high-performance' }}
      >
        <DemandNudge signal={`${t}|${selectedId}|${isolatedId}|${sceneKey}|${asset.id}`} />
        <DemandNudge signal={overlayColors} />
        <OrbitControls
          ref={controlsRef}
          makeDefault
          target={asset.camera.target}
          enableDamping
          dampingFactor={0.08}
          minDistance={1.2}
          maxDistance={asset.camera.maxDistance}
          maxPolarAngle={Math.PI * 0.495}
          autoRotate={autoRotate}
          autoRotateSpeed={0.6}
          onStart={() => setInteracted(true)}
        />
        <CameraRig config={asset.camera} controlsRef={controlsRef} />
        <HomeReturn active={returnHome} config={asset.camera} controlsRef={controlsRef} />
        <ExplodedScene
          key={sceneKey}
          tree={tree}
          manifest={manifest}
          t={t}
          selectedId={selectedId}
          onSelect={(id) => onSelect?.(id ?? null)}
          isolatedId={manifestReady && isolatedId && tree.byId.has(isolatedId) ? isolatedId : null}
          isolateMode="dim"
          showProvenance={false}
          overlayColors={manifestReady ? overlayColors : null}
        />
      </Canvas>

      <div
        ref={barRef}
        className="astage__bar"
        role="toolbar"
        aria-label="Assembly controls"
        style={topOffset !== DEFAULT_TOP ? { top: topOffset } : undefined}
      >
        <div className="astage__assets" role="group" aria-label="Asset">
          {EXPLODED_ASSETS.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`astage__chip${a.id === asset.id ? ' is-on' : ''}`}
              aria-pressed={a.id === asset.id}
              title={a.note}
              onClick={() => { if (a.id !== asset.id) onAssetChange?.(a.id); }}
            >
              {a.label}
            </button>
          ))}
        </div>

        <label className="astage__explode" htmlFor="astage-explode">
          <span className="astage__explode-label">Explode</span>
          <input
            id="astage-explode"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={t}
            onChange={(e) => onTChange?.(Number(e.target.value))}
            aria-valuetext={`${Math.round(t * 100)} percent`}
          />
          <output className="astage__explode-value" htmlFor="astage-explode">{Math.round(t * 100)}%</output>
        </label>

        <button type="button" className="astage__icon" onClick={resetView} aria-label="Reset view" title="Reset view">
          <RotateCcw size={14} strokeWidth={1.8} aria-hidden="true" />
        </button>

        <button type="button" className="astage__open" onClick={onOpenWorkspace} title="Assembly tree, part knowledge, fault trace and ask">
          <Maximize2 size={14} strokeWidth={1.8} aria-hidden="true" />
          <span>Open workspace</span>
        </button>
      </div>

      {caption && (
        <div
          className="astage__caption"
          role="status"
          aria-live="polite"
          style={{ top: belowBar }}
        >
          {caption}
        </div>
      )}

      {node && (
        <section
          className="astage__card"
          aria-label="Selected part"
          style={{ top: belowBar }}
        >
          <div className="astage__card-head">
            <span className="astage__card-level">{LEVEL_LABEL[node.level] ?? node.level}</span>
            <button type="button" className="astage__icon" onClick={() => onSelect?.(null)} aria-label="Clear selection" title="Clear selection">✕</button>
          </div>
          {crumbs.length > 0 && (
            <div className="astage__crumbs">
              {crumbs.map((c) => (
                <button key={c.id} type="button" className="astage__crumb" onClick={() => onSelect?.(c.id)}>{c.name}</button>
              ))}
            </div>
          )}
          <div className="astage__card-name">{node.name}</div>
          <div className="astage__card-id">{node.id}</div>

          {partTelemetry ? (
            <div className="astage__telemetry" aria-live="polite">
              <div className="astage__telemetry-head">
                <span className="astage__telemetry-kicker">Live telemetry</span>
                <span className="astage__telemetry-title">{partTelemetry.title}</span>
              </div>
              <dl className="astage__telemetry-grid">
                {partTelemetry.metrics.map((metric) => (
                  <div key={metric.key} className={`astage__metric${metric.status ? ` is-${metric.status}` : ''}`}>
                    <dt>{metric.label}</dt>
                    <dd>
                      {metric.status && metric.status !== 'good' && (
                        <span className="astage__metric-flag" aria-label={metric.status === 'bad' ? 'Critical' : 'Warning'}>
                          {metric.status === 'bad' ? '■' : '▲'}
                        </span>
                      )}
                      {metric.text}
                      {metric.unit && <span className="astage__metric-unit">{metric.unit === '%' ? '%' : ` ${metric.unit}`}</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : (
            <p className="astage__card-text is-dim">No live telemetry for this part</p>
          )}

          {asset.knowledge === 'part' && summary.phase === 'loading' && (
            <p className="astage__card-text is-dim">Loading part knowledge…</p>
          )}
          {asset.knowledge === 'part' && summary.phase === 'ready' && summary.text && (
            <p className="astage__card-text">{cleanSummary(summary.text)}</p>
          )}
          {asset.knowledge === 'part' && summary.phase === 'unavailable' && (
            <p className="astage__card-text is-dim">Knowledge service unavailable</p>
          )}

          <button type="button" className="astage__card-open" onClick={onOpenWorkspace}>
            Open in workspace
          </button>
        </section>
      )}

      {!error && (loading || !manifestReady || partCount > 0) && (
        <SceneLoadingOverlay
          assetLabel={asset.label}
          manifestLoading={loading || !manifestReady}
          modelUrl={modelUrl}
          partCount={partCount}
          onRetry={() => {
            if (modelUrl) sceneLoad.reset(modelUrl);
            setSceneKey((k) => k + 1);
          }}
        />
      )}
      {error && (
        <div className="astage__error" role="alert">
          <span>Could not load the {asset.label} assembly.</span>
          <button type="button" className="astage__open" onClick={reload}>Retry</button>
        </div>
      )}

      <div className="astage__foot">
        <span className="astage__prov">{PROVENANCE_TEXT}</span>
        {!node && phase === 'ready' && (
          <span className="astage__hint">Click a part · drag to orbit · scroll to zoom</span>
        )}
      </div>
    </div>
  );
}
